/**
 * Buttons (design §7.2): primary, secondary, ghost, destructive, icon.
 *
 * Two sizes so callers stop re-deriving a compact button with `className`
 * overrides (`h-9 px-3.5 text-[13px]` was copy-pasted across the app and drifted).
 * Visible focus with accent ring; 140 ms transition.
 */
import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { FOCUS_RING } from './focus';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'icon';
export type ButtonSize = 'sm' | 'md';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

/** Height + horizontal padding + type scale per size (icon manages its own). */
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3.5 text-[13px]',
  md: 'h-10 px-[18px] text-[14px]',
};

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'rounded-lg bg-acento font-semibold text-white shadow-reposo ' +
    'hover:-translate-y-px hover:bg-acento-tinta hover:shadow-tarjeta-hover ' +
    'active:translate-y-0 active:scale-[0.98] active:shadow-reposo print:hidden',
  secondary:
    'rounded-lg border border-linea-fuerte bg-superficie font-medium text-tinta ' +
    'hover:border-tinta-3 hover:bg-superficie-2 active:scale-[0.98]',
  ghost:
    'rounded-lg font-medium text-tinta-2 hover:bg-superficie-2 hover:text-tinta active:scale-[0.98]',
  destructive:
    'rounded-lg border border-terracota-tinte font-medium text-terracota ' +
    'hover:border-terracota hover:bg-terracota-tinte active:scale-[0.98]',
  icon:
    'h-8 w-8 rounded-lg text-tinta-3 hover:bg-superficie-2 hover:text-tinta active:scale-[0.95] inline-flex items-center justify-center',
};

/** Ghost is a tertiary action: it should not carry a button-sized gutter. */
const GHOST_SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-2.5 text-[13px]',
  md: 'h-10 px-3 text-[14px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', className, type = 'button', ...rest },
  ref,
) {
  const sizing =
    variant === 'icon' ? '' : variant === 'ghost' ? GHOST_SIZES[size] : SIZES[size];
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex cursor-pointer items-center justify-center gap-2 transition-all duration-150 ease-quirurgico disabled:cursor-not-allowed disabled:opacity-50',
        FOCUS_RING,
        sizing,
        VARIANTS[variant],
        className,
      )}
      {...rest}
    />
  );
});
